import { memo, useCallback, type MouseEvent } from 'react'

import { QuantityCounterButtonStyled } from './styles/QuantityCounterButtonStyled'
import { QuantityCounterStyled } from './styles/QuantityCounterStyled'
import { QuantityValueStyled } from './styles/QuantityValueStyled'
import { Icon } from '@ui/elements/Icon'

export interface IQuantityCounterProps {
  /**
   * Current quantity value
   */
  value: number
  /**
   * Callback when quantity changes (increment/decrement)
   */
  onChange: (newValue: number, event: MouseEvent<HTMLButtonElement>) => void
  /**
   * Callback when delete action is triggered (when value is at minimum)
   */
  onDelete?: (event: MouseEvent<HTMLButtonElement>) => void
  /**
   * Maximum allowed value (optional)
   */
  max?: number
  /**
   * Disable all interactions
   */
  disabled?: boolean
  /**
   * Disables the remove button independently of `disabled`.
   * Defaults to `disabled` value if not provided.
   */
  isRemoveDisabled?: boolean
  /**
   * ARIA label for the control
   */
  'aria-label'?: string
  removeButtonAriaLabel?: string
  increaseButtonAriaLabel?: string
  decreaseButtonAriaLabel?: string
  className?: string
}

export const QuantityCounter = memo(
  ({
    value,
    onChange,
    onDelete,
    max,
    disabled = false,
    isRemoveDisabled: isRemoveDisabledProp,
    'aria-label': ariaLabel,
    removeButtonAriaLabel,
    increaseButtonAriaLabel,
    decreaseButtonAriaLabel,
    className,
  }: IQuantityCounterProps) => {
    const isAtOrBelowOne = value <= 1
    const isAtMaximum = max !== undefined && value >= max
    const showRemoveButton = isAtOrBelowOne && !!onDelete
    const numDigits = value.toString().length
    const isRemoveDisabled = isRemoveDisabledProp ?? disabled

    const handleDecrement = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (!disabled && !isAtOrBelowOne) {
          const newValue = value - 1
          onChange(newValue, event)
        }
      },
      [disabled, isAtOrBelowOne, value, onChange]
    )

    const handleIncrement = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (!disabled && !isAtMaximum) {
          const newValue = value + 1
          onChange(newValue, event)
        }
      },
      [disabled, isAtMaximum, value, onChange]
    )

    const handleDelete = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (!isRemoveDisabled) {
          onDelete(event)
        }
      },
      [isRemoveDisabled, onDelete]
    )

    return (
      <QuantityCounterStyled
        className={className}
        role='group'
        aria-label={ariaLabel || 'Quantity counter'}
      >
        {showRemoveButton ? (
          <QuantityCounterButtonStyled
            onClick={handleDelete}
            disabled={isRemoveDisabled}
            aria-label={removeButtonAriaLabel || 'Remove item'}
          >
            <Icon icon='delete' />
          </QuantityCounterButtonStyled>
        ) : (
          <QuantityCounterButtonStyled
            onClick={handleDecrement}
            disabled={disabled || isAtOrBelowOne}
            aria-label={decreaseButtonAriaLabel || 'Decrease quantity'}
          >
            <Icon icon='minus' />
          </QuantityCounterButtonStyled>
        )}
        <QuantityValueStyled numDigits={numDigits}>{value}</QuantityValueStyled>
        <QuantityCounterButtonStyled
          onClick={handleIncrement}
          disabled={disabled || isAtMaximum}
          aria-label={increaseButtonAriaLabel || 'Increase quantity'}
        >
          <Icon icon='add' />
        </QuantityCounterButtonStyled>
      </QuantityCounterStyled>
    )
  }
)

QuantityCounter.displayName = 'QuantityCounter'
